import { Component, EventEmitter, Input, NgZone, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ModalService } from '../../../../services/modal.service';
import { StripeService } from '../../../../services/billing/stripe.service';
import { PaymentMethod } from '../../../../services/billing/payment-method.model';
import { CreditCard } from '../../../../models/entities/credit-card';
import { ConfigGeoRegions } from '../../../../config/geo-regions';

import { MembersService } from '../../../../services/members/members.service';
import { Membership } from '../../../../models/entities/membership';
import { Organization } from '../../../../models/entities/organization';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { StripeCardNumberComponent, StripeService as NgxStripeService } from 'ngx-stripe';
import { StripeCardCvcElementChangeEvent, StripeCardElementOptions, StripeCardExpiryElementChangeEvent, StripeCardNumberElementChangeEvent, StripeElementsOptions } from '@stripe/stripe-js';
import { GrowlerService } from '../../../../services/growler.service';
import { Subscription } from 'rxjs';
import { StripeTokenResponse } from '../../../../services/billing/stripe';


@UntilDestroy()
@Component({
    selector: 'lf-credit-card-collector',
    styleUrls: ['./credit-card-collector.scss'],
    templateUrl: './credit-card-collector.component.html',
})
export class CreditCardCollectorComponent implements OnInit
{
    @Input() organization: Organization = null;
    @Input() buttonText = 'Save credit card';
    @Input() savable = true;
    @Input() doStackColumns = false;
    @Input() member: Membership = null;
    @Input() paymentMethod: PaymentMethod = {
        type: 'CreditCard',
        value: 'new-credit-card',
    };

    @Output() saved: EventEmitter<StripeTokenResponse> = new EventEmitter<StripeTokenResponse>();

    model: FormGroup;
    selectedRegion: { state: string, country: string } = {state: '', country: ''};
    isSaving = false;
    regionChangeSubscriber: Subscription = null;
    requiredError = 'This field is required.';

    /** New stripe stuff */
    @ViewChild(StripeCardNumberComponent) cardGroup: StripeCardNumberComponent;

    cardOptions: StripeCardElementOptions = {
        style: {
            base: {
                iconColor: '#666EE8',
                color: '#3d4e5f80',
                fontWeight: '300',
                fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
                fontSize: '14px',
                '::placeholder': {
                    color: '#CFD7E0',
                },
            },
            invalid: {
                iconColor: '#EB5858',
                color: '#EB5858',
            },
        },
    };

    elementsOptions: StripeElementsOptions = {
        locale: 'en',
    };

    /** Stripe provides the error messages for these on input change */
    cardError = '';
    cvcError = '';
    expiryError = '';
    /** Assume they are empty until a value is given; but don't init to true, need tri state for initial load */
    isCardEmpty = undefined;
    isCvcEmpty = undefined;
    isExpiryEmpty = undefined;

    constructor(
            private _builder: FormBuilder,
            private _growler: GrowlerService,
            private _membersService: MembersService,
            private _modalService: ModalService,
            private _stripeService: StripeService,
            private _ngxStripeService: NgxStripeService,
    ) {
    }

    /** Dynamic error string for the card field */
    get cardErrorString(): string {
        return this.isCardEmpty === true ? this.requiredError : this.cardError;
    }

    /** Dynamic error string for the expiration field */
    get expiryErrorString(): string {
        return this.isExpiryEmpty === true ? this.requiredError : this.expiryError;
    }

    /** Dynamic error string for the cvc field */
    get cvcErrorString(): string {
        return this.isCvcEmpty === true ? this.requiredError : this.cvcError;
    }

    /** Dynamic check whether any card-related (buried in Stripe) fields have errors to block submission */
    get cardGroupHasError(): boolean {
        return !!(this.cardError || this.expiryError || this.isCardEmpty || this.isCvcEmpty || this.isExpiryEmpty);
    }

    ngOnInit() {
        if (this.organization) {
            this.buildModel(this.organization);
        } else if (this.member) {
            this.buildModel(this.member);
        } else {
            this._membersService
                    .getMember(null, null)
                    .pipe(untilDestroyed(this))
                    .subscribe(
                            (member: Membership) => {
                                this.member = member;
                                this.buildModel(this.member);
                            },
                            () => {
                                this.buildModel({});
                            },
                    );
        }
    }

    /** Set error messages or non-empty state on input change */
    onCardChange(event: StripeCardNumberElementChangeEvent) {
        this.cardError = event.error ? event.error.message : '';
        this.isCardEmpty = event.empty;

        if (event && event.brand === 'amex') {
            this.cardError = 'We do not accept payments from American Express credit cards. Please try another card or switch to e-check payments.';
        }
    }

    /** Set error messages or non-empty state on input change */
    onExpiryChange(event: StripeCardExpiryElementChangeEvent) {
        this.expiryError = event.error ? event.error.message : '';
        this.isExpiryEmpty = event.empty;
    }

    /** Set error messages or non-empty state on input change */
    onCvcChange(event: StripeCardCvcElementChangeEvent) {
        this.cvcError = event.error ? event.error.message : '';
        this.isCvcEmpty = event.empty;
    }

    /** Get the form data into shape to send to Stripe */
    exportTokenData() {
        const formData = this.model.value;

        return {
            name: formData.cardholderName,
            address_line1: formData.address1,
            address_line2: formData.address2 || '',
            address_city: formData.city,
            address_state: this.selectedRegion.state,
            address_zip: formData.zip,
            address_country: this.selectedRegion.country,
        };
    }

    /** Send to stripe, get the token, or an error */
    onSubmit() {
        if (this.isSaving) {
            return;
        }

        this.isSaving = true;
        this.markCardElementsAsTouched();
        this.model.markAllAsTouched();

        if (this.cardGroupHasError || !this.model.valid) {
            this.isSaving = false;
            this._growler.error('Oops', 'Please check all required fields');
            return;
        }

        const tokenData = this.exportTokenData();
        this._ngxStripeService
                .createToken(this.cardGroup.element, tokenData)
                .subscribe((result: any) => {
                    if (result.token) {
                        this.isSaving = false;

                        // Use the token
                        this.saved.emit(<StripeTokenResponse>result.token);
                        this._modalService.save(
                                CreditCardCollectorComponent,
                                result.token,
                        );
                    } else if (result.error) {
                        this.isSaving = false;

                        // Error creating the token
                        this._growler.error('Oops', result.error.message);
                    }
                });
    }

    /** Set up the form model */
    buildModel(data: any) {
        this.model = this._builder.group({
            cardholderName: [
                '',
                Validators.required,
            ],
            address1: [
                data.profile && data.profile.address1
                        ? data.profile.address1
                        : null,
                Validators.required,
            ],
            address2: [
                data.profile && data.profile.address2
                        ? data.profile.address2
                        : '',
            ],
            city: [
                data.profile && data.profile.city ? data.profile.city : null,
                Validators.required,
            ],
            region: [
                data.profile && data.profile.state && data.profile.country
                        ? ConfigGeoRegions.findRegion({
                            countryCode: data.profile.country,
                            region: data.profile.state,
                        })
                        : null,
            ],
            zip: [
                data.profile && data.profile.zip ? data.profile.zip : null,
                Validators.required,
            ],
            email: [
                data.user && data.user.email ? data.user.email : null,
                Validators.required,
            ],
        });

        if (data.profile && data.profile.state && data.profile.country) {
            this.selectedRegion.state = data.profile.state;
            this.selectedRegion.country = data.profile.country;
        }

        this.setupRegionChangeSubscriber();
    }

    /** TODO - get rid of needing this; replace lf geo region selector with something better */
    setupRegionChangeSubscriber() {
        this.regionChangeSubscriber = this.model.controls[
                'region'
                ].valueChanges.subscribe(newValue => {
            this.selectedRegion.state = newValue.region;
            this.selectedRegion.country = newValue.countryCode;
        });
    }

    /** Shared code to check if field is touched and invalid, vs putting it all on every error in the markup */
    isFieldInvalid(fieldName: string): boolean {
        return this.model.get(fieldName).invalid && this.model.get(fieldName).touched;
    }

    /** If the stripe elements are still at default undefined state on submit, they are untouched; mark as empty */
    markCardElementsAsTouched() {
        if (this.isCardEmpty === undefined) {
            this.isCardEmpty = true;
        }
        if (this.isCvcEmpty === undefined) {
            this.isCvcEmpty = true;
        }
        if (this.isExpiryEmpty === undefined) {
            this.isExpiryEmpty = true;
        }
    }
}
